import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, EnvelopeSimple, CheckCircle, PencilSimple, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (s) => {
  if (s === 'pago') return 'badge-success';
  if (s === 'cancelado') return 'badge-error';
  return 'badge-warning';
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editPayment, setEditPayment] = useState(null);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: 'Lembrete de pagamento - Tia Karol Sports', amount: '', description: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/students/${id}/payments`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSavePayment = async () => {
    if (!editPayment) return;
    try {
      await axios.put(
        `${API}/admin/students/${id}/payments/${editPayment.source_collection}/${editPayment.id}`,
        {
          payment_status: editPayment.status,
          payment_method: editPayment.payment_method || undefined,
          amount: editPayment.amount !== '' ? Number(editPayment.amount) : undefined,
        },
        { withCredentials: true }
      );
      toast.success('Pagamento atualizado');
      setEditPayment(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.message && !emailForm.amount) {
      toast.error('Informe pelo menos a mensagem ou o valor');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/admin/students/${id}/charge-email`,
        { subject: emailForm.subject, message: emailForm.message, amount: emailForm.amount || null, description: emailForm.description },
        { withCredentials: true }
      );
      toast.success(`Email enviado para ${data.student.email}`);
      setEmailDialog(false);
      setEmailForm({ subject: 'Lembrete de pagamento - Tia Karol Sports', amount: '', description: '', message: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const filtered = (data?.payments || []).filter((p) => {
    if (statusFilter !== 'all' && (p.status || 'pendente') !== statusFilter) return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return (p.type || '').toLowerCase().includes(t) || (p.description || '').toLowerCase().includes(t);
  });

  const fmtDate = (s) => {
    if (!s || s === '-') return '-';
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleDateString('pt-BR');
    } catch { return s; }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="student-detail-page">
        <button onClick={() => navigate('/students')} className="inline-flex items-center text-[#73706A] hover:text-[#E85D04] text-sm" data-testid="student-detail-back">
          <ArrowLeft size={16} className="mr-1" /> Voltar para Alunos
        </button>

        {loading || !data ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="page-title">{data.student.name}</h1>
                <p className="text-sm text-[#73706A]">{data.student.email} &middot; {data.student.phone || '-'} &middot; Plano: {data.student.plan || '-'}</p>
              </div>
              <Button onClick={() => setEmailDialog(true)} className="bg-[#0077B6] hover:bg-[#03045E] text-white" data-testid="send-charge-email-btn">
                <EnvelopeSimple size={18} className="mr-2" /> Enviar e-mail de cobranca
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <button onClick={() => setStatusFilter('all')} className={`stat-card text-left transition-all ${statusFilter === 'all' ? 'ring-2 ring-[#E85D04]' : ''}`}>
                <p className="text-sm text-[#73706A]">Total registros</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{data.count}</p>
              </button>
              <button onClick={() => setStatusFilter('pago')} className={`stat-card text-left transition-all ${statusFilter === 'pago' ? 'ring-2 ring-[#2D6A4F]' : ''}`}>
                <p className="text-sm text-[#73706A]">Total Pago</p>
                <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">${data.total_paid.toFixed(2)}</p>
              </button>
              <button onClick={() => setStatusFilter('pendente')} className={`stat-card text-left transition-all ${statusFilter === 'pendente' ? 'ring-2 ring-[#E85D04]' : ''}`}>
                <p className="text-sm text-[#73706A]">Total Pendente</p>
                <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">${data.total_pending.toFixed(2)}</p>
              </button>
              <div className="stat-card">
                <p className="text-sm text-[#73706A]">Balanco</p>
                <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">${(data.total_paid - data.total_pending).toFixed(2)}</p>
              </div>
            </div>

            <div className="relative max-w-md">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
              <Input placeholder="Buscar por tipo ou descricao..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="student-detail-search" />
            </div>

            <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-[#73706A]">{statusFilter !== 'all' || search ? 'Nenhum registro encontrado para o filtro' : 'Sem pagamentos registrados'}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F5F2EB]">
                        <th className="table-header">Tipo</th>
                        <th className="table-header">Descricao</th>
                        <th className="table-header">Data</th>
                        <th className="table-header">Valor</th>
                        <th className="table-header">Metodo</th>
                        <th className="table-header">Status</th>
                        <th className="table-header text-right">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p, i) => (
                        <tr key={`${p.source_collection}-${p.id}`} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`student-detail-row-${i}`}>
                          <td className="table-cell font-medium text-[#1A1A1A]">{p.type}</td>
                          <td className="table-cell text-xs text-[#73706A] max-w-[220px] truncate" title={p.description}>{p.description}</td>
                          <td className="table-cell text-xs">{fmtDate(p.date)}</td>
                          <td className="table-cell font-medium text-[#2D6A4F]">$ {(Number(p.amount) || 0).toFixed(2)}</td>
                          <td className="table-cell text-xs text-[#73706A]">{p.payment_method || '-'}</td>
                          <td className="table-cell">
                            <span className={`badge ${statusBadge(p.status)}`}>{p.status || 'pendente'}</span>
                          </td>
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-1">
                              {p.status !== 'pago' && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={async () => {
                                    if (!window.confirm('Marcar este pagamento como PAGO?')) return;
                                    await axios.put(
                                      `${API}/admin/students/${id}/payments/${p.source_collection}/${p.id}`,
                                      { payment_status: 'pago', payment_method: 'manual' },
                                      { withCredentials: true }
                                    );
                                    toast.success('Marcado como pago');
                                    load();
                                  }}
                                  className="text-[#2D6A4F] hover:bg-green-50"
                                  data-testid={`mark-paid-${i}`}
                                  title="Marcar como pago"
                                >
                                  <CheckCircle size={18} />
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => setEditPayment({ ...p, amount: p.amount, status: p.status || 'pendente' })}
                                className="text-[#73706A] hover:bg-gray-100"
                                data-testid={`edit-payment-${i}`}
                                title="Editar"
                              >
                                <PencilSimple size={18} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Edit payment dialog */}
        <Dialog open={!!editPayment} onOpenChange={(o) => !o && setEditPayment(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar pagamento</DialogTitle>
            </DialogHeader>
            {editPayment && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Input value={editPayment.type} disabled />
                </div>
                <div>
                  <Label className="text-xs">Descricao</Label>
                  <Input value={editPayment.description} disabled />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-amount" className="text-xs">Valor ($)</Label>
                    <Input id="edit-amount" type="number" step="0.01" value={editPayment.amount} onChange={(e) => setEditPayment((p) => ({ ...p, amount: e.target.value }))} data-testid="edit-payment-amount" />
                  </div>
                  <div>
                    <Label htmlFor="edit-status" className="text-xs">Status</Label>
                    <select id="edit-status" value={editPayment.status} onChange={(e) => setEditPayment((p) => ({ ...p, status: e.target.value }))} className="w-full border border-[#E6E2D8] rounded-md px-3 py-2 text-sm" data-testid="edit-payment-status">
                      <option value="pendente">pendente</option>
                      <option value="pago">pago</option>
                      <option value="cancelado">cancelado</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-method" className="text-xs">Metodo</Label>
                  <Input id="edit-method" value={editPayment.payment_method || ''} onChange={(e) => setEditPayment((p) => ({ ...p, payment_method: e.target.value }))} placeholder="manual / cartao / Zelle / -" data-testid="edit-payment-method" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPayment(null)}>Cancelar</Button>
              <Button onClick={handleSavePayment} className="bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="edit-payment-save">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Charge email dialog */}
        <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar cobranca por e-mail</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Para</Label>
                <Input value={data?.student.email || ''} disabled />
              </div>
              <div>
                <Label htmlFor="email-subject" className="text-xs">Assunto</Label>
                <Input id="email-subject" value={emailForm.subject} onChange={(e) => setEmailForm((p) => ({ ...p, subject: e.target.value }))} data-testid="email-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email-amount" className="text-xs">Valor ($) - opcional</Label>
                  <Input id="email-amount" type="number" step="0.01" value={emailForm.amount} onChange={(e) => setEmailForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Ex: 90.00" data-testid="email-amount-input" />
                </div>
                <div>
                  <Label htmlFor="email-desc" className="text-xs">Descricao da cobranca</Label>
                  <Input id="email-desc" value={emailForm.description} onChange={(e) => setEmailForm((p) => ({ ...p, description: e.target.value }))} placeholder="Ex: Mensalidade jun/2026" data-testid="email-description-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="email-msg" className="text-xs">Mensagem</Label>
                <Textarea id="email-msg" rows={5} value={emailForm.message} onChange={(e) => setEmailForm((p) => ({ ...p, message: e.target.value }))} placeholder={`Ola ${data?.student.name || 'Atleta'}, sua mensalidade de junho esta pendente...`} data-testid="email-message-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialog(false)}>Cancelar</Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-[#0077B6] hover:bg-[#03045E] text-white" data-testid="email-send-btn">
                {sendingEmail ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <EnvelopeSimple size={16} className="mr-2" />}
                Enviar e-mail
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
